// admin/src/features/preview/subject.ts
//
// What a preview shows. Exercise media is laid out exactly like the app's
// ExerciseHeroCarousel (src/features/exercises/ExerciseHeroCarousel.tsx):
// the demo photo, then every video, then the extra images, phone
// rendition only (src/features/media/repository.ts filters on it).
import type { ExerciseMediaRow } from '../../lib/types';

export type HeroItem = { key: string; kind: 'photo'; url: string } | { key: string; kind: 'video'; url: string; poster: string | null };

export type ExerciseInfo = {
  name: string;
  muscleGroup: string;
  muscles: string[];
  sport: string;
  description: string | null;
  equipment: Array<{ id: string; name: string; photoUrl: string | null }>;
};

export type ExerciseSubject = ExerciseInfo & { kind: 'exercise'; items: HeroItem[]; startIndex: number };
export type EquipmentSubject = { kind: 'equipment'; name: string; photoUrl: string };
export type CoverSubject = { kind: 'cover'; slot: string; url: string; focalX: number; focalY: number };
export type PreviewSubject = ExerciseSubject | EquipmentSubject | CoverSubject;

/** A not-yet-uploaded item, shown where the app will put it. */
export type PendingHeroMedia = { kind: 'demo_photo' | 'image'; url: string } | { kind: 'video'; url: string; poster: string | null };

type UrlFor = (path: string | null | undefined) => string | null;

export function buildHeroItems(
  rows: ExerciseMediaRow[],
  urlFor: UrlFor,
  focus?: { pending: PendingHeroMedia } | { rowId: string },
): { items: HeroItem[]; startIndex: number } {
  const phone = rows.filter((r) => r.rendition === 'phone').sort((a, b) => a.sort - b.sort);
  const pending = focus && 'pending' in focus ? focus.pending : null;
  const items: HeroItem[] = [];
  let startIndex = 0;

  const demo = phone.find((r) => r.kind === 'demo_photo');
  if (pending?.kind === 'demo_photo') {
    items.push({ key: 'pending', kind: 'photo', url: pending.url });
  } else if (demo) {
    const url = urlFor(demo.path);
    if (url) items.push({ key: demo.id, kind: 'photo', url });
  }

  for (const row of phone.filter((r) => r.kind === 'video')) {
    const url = urlFor(row.path);
    if (url) items.push({ key: row.id, kind: 'video', url, poster: urlFor(row.poster_path) });
  }
  if (pending?.kind === 'video') {
    items.push({ key: 'pending', kind: 'video', url: pending.url, poster: pending.poster });
  }

  for (const row of phone.filter((r) => r.kind === 'image')) {
    const url = urlFor(row.path);
    if (url) items.push({ key: row.id, kind: 'photo', url });
  }
  if (pending?.kind === 'image') {
    items.push({ key: 'pending', kind: 'photo', url: pending.url });
  }

  const focusKey = pending ? 'pending' : focus && 'rowId' in focus ? focus.rowId : null;
  if (focusKey) startIndex = Math.max(0, items.findIndex((i) => i.key === focusKey));
  return { items, startIndex };
}

/** The still the watch's How-to page would show for the previewed item: the photo, or a video's poster. */
export function watchStill(items: HeroItem[], index: number): string | null {
  const item = items[index] ?? items[0];
  if (!item) return null;
  if (item.kind === 'photo') return item.url;
  return item.poster ?? items.find((i): i is Extract<HeroItem, { kind: 'photo' }> => i.kind === 'photo')?.url ?? null;
}

/** src/theme/colors.ts `muscleColor`. */
export const MUSCLE_COLOR: Record<string, string> = {
  chest: '#47C9FF',
  back: '#2EE6A8',
  shoulders: '#8B6CFF',
  arms: '#F49AC8',
  forearms: '#F49AC8',
  legs: '#F5C451',
  calves: '#F5C451',
  glutes: '#FF9F5A',
  core: '#7CFF6B',
  neck: '#8B6CFF',
  full_body: '#2EE6A8',
};

export function muscleTint(group: string): string {
  return MUSCLE_COLOR[group] ?? MUSCLE_COLOR.full_body;
}

/** Cover slot -> its platform's gradient (src/theme/colors.ts accentGradient via platform color). */
export function coverGradient(slot: string): [string, string] {
  if (slot.includes('swim')) return ['#47C9FF', '#3A6BFF'];
  if (slot.includes('cardio')) return ['#FFB25A', '#FF6A4D'];
  if (slot.includes('mobility')) return ['#F49AC8', '#B07CFF'];
  return ['#2EE6A8', '#14B8A6'];
}
